import React from 'react';
import { 
  Star, 
  TrendingUp, 
  Zap, 
  Package, // Changed from LayoutGrid for logistics
  Truck,   // Added for delivery context
  Store,   // Added for retail context
  Percent, // Added for discounts
  BarChart3, 
  MousePointer2, 
} from 'lucide-react';
// 1. Import the new cursor component
import MouseFollower from './Mousefollower'; 

const HeroSection = () => {
  return (
    <section className="min-h-screen bg-white flex items-center justify-center p-4 md:p-8 overflow-hidden font-sans text-slate-900 relative cursor-none md:cursor-default">
       {/* 2. Add the MouseFollower here. */}
       <MouseFollower />

      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        
        {/* --- LEFT COLUMN: Text Content --- */}
        <div className="flex flex-col items-start space-y-6 z-10 ml-10">
          {/* Badge */}
          <div className="inline-flex items-center px-3 py-1 rounded-full border border-orange-100 bg-orange-50/50">
            <span className="text-orange-500 text-xs font-bold tracking-wide uppercase">#1 B2B Wholesale Platform</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.1] font-extrabold text-slate-900">
            Source Smarter <br />
            <span className="font-serif italic text-[#FF5733]">Maximize Margins</span>
          </h1>

          {/* Subheadline */}
          <p className="text-slate-500 text-lg md:text-xl max-w-lg leading-relaxed">
            Daily Go connects retailers directly to manufacturers. Get factory prices, 
            bulk discounts, and next-day delivery to keep your shelves stocked.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto pt-2 relative z-20">
            <button className="px-8 py-4 bg-[#FF5733] hover:bg-[#e04828] text-white font-semibold rounded-lg shadow-lg shadow-orange-200 transition-all transform hover:-translate-y-0.5 hover:scale-105 active:scale-95 flex items-center justify-center gap-2">
              <Store className="w-5 h-5" />
              Explore Catalog
            </button>
            <button className="px-8 py-4 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-semibold rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 hover:bg-slate-50 active:bg-slate-100">
              Become a Seller
              <span className="text-slate-400">→</span>
            </button>
          </div>

          {/* Social Proof / Reviews */}
          <div className="flex items-center gap-6 pt-4">
            <div className="flex -space-x-3">
              {[1, 2, 3].map((i) => (
                <img 
                  key={i}
                  src={`https://i.pravatar.cc/100?img=${12 + i}`} 
                  alt="Retailer" 
                  className="w-10 h-10 rounded-full border-2 border-white object-cover select-none" 
                />
              ))}
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1 text-yellow-400 select-none">
                <Star className="w-4 h-4 fill-current" />
                <span className="text-slate-900 font-bold text-sm">4.9</span>
                <span className="text-slate-400 text-sm font-normal">(12K+ Retailers)</span>
              </div>
            </div>
          </div>
          
          <p className="text-slate-400 text-sm mt-8">Supplying 50+ Cities across India</p>
        </div>

        {/* --- RIGHT COLUMN: Visual Composition --- */}
        <div className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] flex items-center justify-center lg:justify-end select-none pointer-events-none md:pointer-events-auto">
          
          {/* Background Decorative Blob/Shape */}
          <div className="absolute top-10 right-0 lg:right-10 w-[90%] h-[80%] bg-[#FFF6F4] rounded-[3rem] -z-10 transform rotate-1"></div>
          
          {/* Background Grid Pattern */}
          <div className="absolute top-20 right-20 grid grid-cols-6 gap-2 opacity-20 pointer-events-none">
            {[...Array(24)].map((_, i) => (
              <div key={i} className="w-1 h-1 bg-orange-400 rounded-full"></div>
            ))}
          </div>

          {/* Main Image Card (Warehouse/Logistics vibe) */}
          <div className="relative z-10 w-64 md:w-80 lg:w-96 bg-white p-3 rounded-2xl shadow-2xl transform translate-y-[-20px] transition-transform hover:scale-[1.02] duration-500">
             {/* Tooltip on top of image */}
            <div className="absolute -left-12 top-10 z-20 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-slate-100 flex items-center gap-2 animate-bounce-slow">
              <div className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center">
                 <Truck className="w-3 h-3 text-orange-500" />
              </div>
              <span className="text-xs font-semibold text-slate-700">Dispatching Order #402...</span>
              <MousePointer2 className="w-4 h-4 text-[#FF5733] fill-[#FF5733] absolute -bottom-3 right-0" />
            </div>

            <img 
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=800" 
              alt="Warehouse Manager" 
              className="w-full h-64 md:h-80 object-cover rounded-xl bg-slate-100"
            />
          </div>

          {/* Floating Widget: Weekly Orders */}
          <div className="absolute left-0 bottom-20 md:left-10 md:bottom-32 bg-white p-4 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-50 w-40 z-20 animate-fade-in-up transition-transform hover:translate-y-[-5px] duration-300">
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs text-slate-500 font-medium">Orders Delivered</span>
              <span className="text-[10px] text-green-500 font-bold flex items-center bg-green-50 px-1 rounded">
                <TrendingUp className="w-3 h-3 mr-1" /> +24%
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">8,450</h3>
            <p className="text-[10px] text-slate-400 mt-1">This Month</p>
          </div>

          {/* Floating Widget: Stock Efficiency (Gauge) */}
          <div className="absolute right-10 bottom-10 md:right-20 md:bottom-16 bg-white p-4 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-50 w-44 z-20 transition-transform hover:translate-y-[-5px] duration-300 delay-75">
             <div className="flex justify-between items-start mb-2">
              <span className="text-xs text-slate-500 font-medium">Stock Efficiency</span>
              <span className="text-[10px] text-green-500 font-bold flex items-center">
                <TrendingUp className="w-3 h-3 mr-1" /> Optimal
              </span>
            </div>
            {/* CSS Only Gauge chart representation */}
            <div className="relative w-24 h-12 overflow-hidden mx-auto mt-2">
               <div className="w-24 h-24 rounded-full border-[10px] border-slate-100 border-t-[#FF5733] border-r-[#FF5733] transform -rotate-12"></div>
            </div>
            <div className="text-center -mt-6">
                <span className="text-xl font-bold text-slate-900">98%</span>
            </div>
          </div>

          {/* Floating Widget: Verified Seller Tag */}
          <div className="absolute top-1/2 right-0 transform translate-x-2 md:translate-x-0 bg-[#E85C4A] text-white px-4 py-2 rounded-full shadow-lg shadow-orange-200 flex items-center gap-2 z-30 hover:scale-110 transition-transform">
             <Store className="w-4 h-4 text-white" />
             <span className="text-sm font-semibold">Verified Sellers</span>
          </div>

           {/* Floating Widget: Logo Mark Top */}
           <div className="absolute top-20 left-1/2 md:left-40 bg-white p-3 rounded-xl shadow-lg border border-slate-50 z-0 animate-spin-slow">
             <div className="w-6 h-6 bg-gradient-to-tr from-[#FF5733] to-orange-300 rounded-sm transform rotate-45"></div>
          </div>

          {/* Vertical Toolbar (Far Right) */}
          <div className="absolute right-0 top-10 md:top-20 hidden lg:flex flex-col gap-6 bg-white py-6 px-3 rounded-l-3xl shadow-lg border border-r-0 border-slate-100 z-40">
             <div className="p-2 bg-orange-50 rounded-lg cursor-pointer hover:scale-110 transition-transform" title="Products"><Package className="w-5 h-5 text-[#FF5733]" /></div>
             <div className="p-2 hover:bg-slate-50 rounded-lg transition-all cursor-pointer hover:scale-110" title="Analytics"><BarChart3 className="w-5 h-5 text-slate-400 hover:text-[#FF5733]" /></div>
             <div className="p-2 hover:bg-slate-50 rounded-lg transition-all cursor-pointer hover:scale-110" title="Discounts"><Percent className="w-5 h-5 text-slate-400 hover:text-[#FF5733]" /></div>
             <div className="p-2 hover:bg-slate-50 rounded-lg transition-all cursor-pointer hover:scale-110" title="Delivery"><Truck className="w-5 h-5 text-slate-400 hover:text-[#FF5733]" /></div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;